import {
  ChevronDown,
  ChevronUp,
  Clock,
  GripVertical,
  MoreVertical,
  Plus,
  User,
} from "lucide-react"
import { useState } from "react"
import { useNavigate } from "react-router-dom"
import { cx } from "../lib/cx"
import type { Project, ProjectTask } from "../types"
import Card from "./Card"
import StatusPill from "./StatusPill"

type KanbanBoardProps = {
  projects: Project[]
  onProjectStatusChange: (projectId: string, newStatus: Project["status"]) => void
  onTaskStatusChange: (projectId: string, taskId: string, newStatus: ProjectTask["status"]) => void
  onAddTask?: (projectId: string, title: string) => void
}

const columns: Array<{ id: Project["status"]; label: string; color: string }> = [
  { id: "Planned", label: "Planned", color: "bg-slate-100" },
  { id: "Active", label: "Active", color: "bg-blue-100" },
  { id: "Review", label: "Review", color: "bg-yellow-100" },
  { id: "Complete", label: "Complete", color: "bg-green-100" },
]

const taskStatusColors: Record<ProjectTask["status"], string> = {
  todo: "bg-slate-100 text-slate-700",
  in_progress: "bg-blue-100 text-blue-700",
  review: "bg-yellow-100 text-yellow-700",
  done: "bg-green-100 text-green-700",
}

const marginTone = (
  signal: string,
): "success" | "warning" | "danger" | "neutral" => {
  if (signal === "hot") return "danger"
  if (signal === "growing") return "success"
  return "neutral"
}

const KanbanCard = ({
  project,
  onTaskStatusChange,
  onAddTask,
}: {
  project: Project
  onTaskStatusChange: (projectId: string, taskId: string, newStatus: ProjectTask["status"]) => void
  onAddTask?: (projectId: string, title: string) => void
}) => {
  const navigate = useNavigate()
  const [isExpanded, setIsExpanded] = useState(false)
  const [draggedTaskId, setDraggedTaskId] = useState<string | null>(null)
  const [showNewTask, setShowNewTask] = useState(false)
  const [newTaskTitle, setNewTaskTitle] = useState("")
  const [showMenu, setShowMenu] = useState(false)

  const tasksByStatus = {
    todo: project.subtasks.filter((t) => t.status === "todo"),
    in_progress: project.subtasks.filter((t) => t.status === "in_progress"),
    review: project.subtasks.filter((t) => t.status === "review"),
    done: project.subtasks.filter((t) => t.status === "done"),
  }

  const handleTaskDragStart = (e: React.DragEvent, taskId: string) => {
    e.stopPropagation() // Prevent project drag
    setDraggedTaskId(taskId)
    e.dataTransfer.effectAllowed = "move"
    e.dataTransfer.setData("taskId", taskId)
    e.dataTransfer.setData("projectId", project.id)
  }

  const handleTaskDrop = (e: React.DragEvent, targetStatus: ProjectTask["status"]) => {
    e.stopPropagation() // Prevent project drop
    e.preventDefault()
    
    const taskId = e.dataTransfer.getData("taskId") || draggedTaskId
    const sourceProjectId = e.dataTransfer.getData("projectId")
    
    // Only process if it's from the same project
    if (taskId && sourceProjectId === project.id) {
      onTaskStatusChange(project.id, taskId, targetStatus as ProjectTask["status"])
      setDraggedTaskId(null)
    }
  }

  const handleTaskDragOver = (e: React.DragEvent) => {
    e.stopPropagation() // Prevent project drag over
    e.preventDefault()
    e.dataTransfer.dropEffect = "move"
  }

  const handleProjectDragStart = (e: React.DragEvent) => {
    e.dataTransfer.setData("projectId", project.id)
    e.dataTransfer.setData("currentStatus", project.status)
    e.dataTransfer.effectAllowed = "move"
  }

  return (
    <div
      draggable
      onDragStart={handleProjectDragStart}
      className="group relative cursor-move rounded-2xl border border-line bg-white p-4 shadow-sm transition hover:shadow-md"
    >
      {/* Drag handle */}
      <div className="absolute left-2 top-2 opacity-0 transition group-hover:opacity-100">
        <GripVertical className="h-4 w-4 text-stone" />
      </div>

      {/* Header */}
      <div className="pl-6">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <h3
              className="cursor-pointer font-semibold text-slate-900 hover:text-moss"
              onClick={(e) => { e.stopPropagation(); navigate(`/projects/${project.id}`) }}
            >{project.name}</h3>
            <p className="mt-1 text-xs text-stone">{project.client}</p>
          </div>
          <div className="relative">
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); setShowMenu(!showMenu) }}
              className="flex h-6 w-6 items-center justify-center rounded text-stone hover:bg-slate-100"
            >
              <MoreVertical className="h-4 w-4" />
            </button>
            {showMenu && (
              <div className="absolute right-0 top-full z-20 mt-1 w-40 rounded-xl border border-line bg-white py-1 shadow-xl">
                <div className="px-3 py-2 text-xs text-stone border-b border-line">
                  {project.hoursUsed}/{project.budgetHours}h budget
                </div>
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); navigate(`/projects/${project.id}`); setShowMenu(false) }}
                  className="flex w-full items-center px-3 py-2 text-xs text-slate-700 hover:bg-slate-50"
                >
                  View Details
                </button>
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); setIsExpanded(true); setShowMenu(false) }}
                  className="flex w-full items-center px-3 py-2 text-xs text-slate-700 hover:bg-slate-50"
                >
                  View tasks
                </button>
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); setShowNewTask(true); setIsExpanded(true); setShowMenu(false) }}
                  className="flex w-full items-center px-3 py-2 text-xs text-slate-700 hover:bg-slate-50"
                >
                  Add task
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Assignees */}
        <div className="mt-2 flex items-center gap-1.5">
          {project.assignees.slice(0, 3).map((assignee) => (
            <div
              key={assignee}
              className="flex h-6 w-6 items-center justify-center rounded-full bg-moss/10 text-xs font-medium text-moss"
              title={assignee}
            >
              {assignee[0]}
            </div>
          ))}
          {project.assignees.length > 3 && (
            <span className="text-xs text-stone">+{project.assignees.length - 3}</span>
          )}
        </div>

        {/* Budget indicator */}
        <div className="mt-3 flex items-center justify-between text-xs">
          <div className="flex items-center gap-1 text-stone">
            <Clock className="h-3 w-3" />
            <span>
              {project.hoursUsed}/{project.budgetHours} hrs
            </span>
          </div>
          <StatusPill
            tone={marginTone(project.marginSignal)}
            label={
              project.marginSignal === "hot"
                ? "Eating profit"
                : project.marginSignal === "growing"
                  ? "Printing money"
                  : "On track"
            }
          />
        </div>

        {/* Progress bar */}
        <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-slate-100">
          <div
            className="h-full bg-moss transition-all"
            style={{ width: `${(project.tasksDone / project.tasksTotal) * 100}%` }}
          />
        </div>
      </div>

      {/* Subtasks section */}
      <div className="mt-4 border-t border-line pt-3">
        <button
          type="button"
          onClick={() => setIsExpanded(!isExpanded)}
          className="flex w-full items-center justify-between text-xs font-medium text-slate-700 hover:text-slate-900"
        >
          <span>
            Tasks ({project.subtasks.length}) · Done ({tasksByStatus.done.length})
          </span>
          {isExpanded ? (
            <ChevronUp className="h-4 w-4" />
          ) : (
            <ChevronDown className="h-4 w-4" />
          )}
        </button>

        {isExpanded && (
          <div className="mt-3 space-y-3">
            {/* Task status columns */}
            {Object.entries(tasksByStatus).map(([status, tasks]) => {
              if (tasks.length === 0 && status !== "todo") return null

              return (
                <div key={status} className="space-y-1.5">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-medium capitalize text-stone">
                      {status.replace("_", " ")}
                    </span>
                    <span className="text-xs text-stone">{tasks.length}</span>
                  </div>
                  <div
                    className={cx(
                      "min-h-[40px] rounded-lg border-2 border-dashed p-2 transition",
                      draggedTaskId
                        ? "border-moss bg-moss/5"
                        : "border-transparent",
                    )}
                    onDragOver={handleTaskDragOver}
                    onDrop={(e) => {
                      const taskStatus = status as ProjectTask["status"]
                      handleTaskDrop(e, taskStatus)
                    }}
                  >
                    {tasks.map((task) => (
                      <div
                        key={task.id}
                        draggable
                        onDragStart={(e) => {
                          handleTaskDragStart(e, task.id)
                          // Visual feedback
                          const target = e.currentTarget as HTMLElement
                          target.style.opacity = "0.5"
                        }}
                        onDragEnd={(e) => {
                          // Reset visual feedback
                          const target = e.currentTarget as HTMLElement
                          target.style.opacity = "1"
                          setDraggedTaskId(null)
                        }}
                        className={cx(
                          "mb-1.5 flex cursor-move items-start gap-2 rounded-lg border border-line bg-white p-2 text-xs transition hover:shadow-sm",
                          draggedTaskId === task.id && "opacity-50",
                        )}
                      >
                        <div className="mt-0.5 flex-1">
                          <p className="font-medium text-slate-900">{task.title}</p>
                          {task.description && (
                            <p className="mt-0.5 text-stone">{task.description}</p>
                          )}
                          <div className="mt-1.5 flex items-center gap-2">
                            {task.assignee && (
                              <div className="flex items-center gap-1 text-stone">
                                <User className="h-3 w-3" />
                                <span>{task.assignee}</span>
                              </div>
                            )}
                            {task.estimatedHours && (
                              <div className="flex items-center gap-1 text-stone">
                                <Clock className="h-3 w-3" />
                                <span>
                                  {task.actualHours || 0}/{task.estimatedHours}h
                                </span>
                              </div>
                            )}
                            {task.priority && (
                              <span
                                className={cx(
                                  "rounded-full px-1.5 py-0.5 text-xs font-medium",
                                  task.priority === "high"
                                    ? "bg-rose-100 text-rose-700"
                                    : task.priority === "medium"
                                      ? "bg-yellow-100 text-yellow-700"
                                      : "bg-slate-100 text-slate-700",
                                )}
                              >
                                {task.priority}
                              </span>
                            )}
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation()
                            const cycle: ProjectTask["status"][] = ["todo", "in_progress", "review", "done"]
                            const idx = cycle.indexOf(task.status)
                            const next = cycle[(idx + 1) % cycle.length]
                            onTaskStatusChange(project.id, task.id, next)
                          }}
                          className={cx(
                            "rounded px-1.5 py-0.5 text-xs font-medium hover:opacity-80",
                            taskStatusColors[task.status],
                          )}
                        >
                          {task.status.replace("_", " ")}
                        </button>
                      </div>
                    ))}
                    {tasks.length === 0 && (
                      <div className="py-2 text-center text-xs text-stone">
                        Drop tasks here
                      </div>
                    )}
                  </div>
                </div>
              )
            })}

            {/* Add task */}
            {showNewTask ? (
              <div className="flex gap-2">
                <input
                  type="text"
                  value={newTaskTitle}
                  onChange={(e) => setNewTaskTitle(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && newTaskTitle.trim() && onAddTask) {
                      onAddTask(project.id, newTaskTitle.trim())
                      setNewTaskTitle("")
                      setShowNewTask(false)
                    }
                    if (e.key === "Escape") {
                      setNewTaskTitle("")
                      setShowNewTask(false)
                    }
                  }}
                  placeholder="Task title..."
                  className="flex-1 rounded-lg border border-line bg-white px-2 py-1.5 text-xs outline-none focus:border-moss"
                  autoFocus
                />
                <button
                  type="button"
                  onClick={() => {
                    if (newTaskTitle.trim() && onAddTask) {
                      onAddTask(project.id, newTaskTitle.trim())
                      setNewTaskTitle("")
                      setShowNewTask(false)
                    }
                  }}
                  className="rounded-lg bg-moss px-2 py-1.5 text-xs text-white"
                >
                  Add
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => setShowNewTask(true)}
                className="flex w-full items-center gap-2 rounded-lg border border-dashed border-line bg-slate-50 px-3 py-2 text-xs font-medium text-stone transition hover:bg-slate-100 hover:text-slate-700"
              >
                <Plus className="h-3.5 w-3.5" />
                Add Task
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

const KanbanBoard = ({
  projects,
  onProjectStatusChange,
  onTaskStatusChange,
  onAddTask,
}: KanbanBoardProps) => {
  const [draggedProjectId, setDraggedProjectId] = useState<string | null>(null)
  const [draggedFromColumn, setDraggedFromColumn] = useState<Project["status"] | null>(null)

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    e.dataTransfer.dropEffect = "move"
  }

  const handleDrop = (e: React.DragEvent, targetStatus: Project["status"]) => {
    e.preventDefault()
    e.stopPropagation()

    const projectId = e.dataTransfer.getData("projectId")
    const currentStatus = e.dataTransfer.getData("currentStatus") as Project["status"]

    if (projectId && currentStatus && currentStatus !== targetStatus) {
      onProjectStatusChange(projectId, targetStatus)
    }

    setDraggedProjectId(null)
    setDraggedFromColumn(null)
  }

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault()
    const projectId = e.dataTransfer.getData("projectId")
    const currentStatus = e.dataTransfer.getData("currentStatus") as Project["status"]
    if (projectId) {
      setDraggedProjectId(projectId)
      setDraggedFromColumn(currentStatus)
    }
  }

  return (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-4">
      {columns.map((column) => {
        const columnProjects = projects.filter((p) => p.status === column.id)

        return (
          <div key={column.id} className="flex flex-col">
            <Card className="mb-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className={cx("h-2 w-2 rounded-full", column.color)} />
                  <p className="text-sm font-semibold uppercase tracking-wide text-slate-700">
                    {column.label}
                  </p>
                </div>
                <StatusPill tone="neutral" label={columnProjects.length.toString()} />
              </div>
            </Card>

            <div
              className={cx(
                "flex-1 rounded-xl border-2 border-dashed p-3 transition",
                draggedProjectId && draggedFromColumn !== column.id
                  ? "border-moss bg-moss/5"
                  : "border-transparent",
              )}
              onDragOver={handleDragOver}
              onDragEnter={handleDragEnter}
              onDrop={(e) => handleDrop(e, column.id)}
            >
              <div className="space-y-3">
                {columnProjects.map((project) => (
                  <KanbanCard
                    key={project.id}
                    project={project}
                    onTaskStatusChange={(projectId: string, taskId: string, newStatus: ProjectTask["status"]) =>
                      onTaskStatusChange(projectId, taskId, newStatus)
                    }
                    onAddTask={onAddTask}
                  />
                ))}
                {columnProjects.length === 0 && (
                  <div className="py-8 text-center text-sm text-stone">
                    {draggedProjectId ? "Drop project here" : "No projects"}
                  </div>
                )}
              </div>
            </div>
          </div>
        )
      })}
    </div>
  )
}

export default KanbanBoard
